import React, { useState, useEffect } from 'react';
import { User, Lock, CreditCard, Save, Loader2, FileText, Download } from 'lucide-react';

export default function MitarbeiterEinstellungen() {
  const [formData, setFormData] = useState({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    iban: '',
    taxId: '',
    socialSecurityNum: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('tester_token');
      const response = await fetch('/api/employee/profile', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setFormData(prev => ({
          ...prev,
          email: data.email,
          iban: data.iban || '',
          taxId: data.tax_id || '',
          socialSecurityNum: data.social_security_num || ''
        }));
      }
    } catch (err) { console.error(err); } finally { setIsLoading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    if (!formData.currentPassword) {
      return setMessage({ type: 'error', text: 'Bitte gib dein aktuelles Passwort zur Bestätigung ein.' });
    }
    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      return setMessage({ type: 'error', text: 'Die neuen Passwörter stimmen nicht überein.' });
    }
    setIsSaving(true);
    try {
      const token = localStorage.getItem('tester_token');
      const response = await fetch('/api/employee/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          email: formData.email,
          current_password: formData.currentPassword,
          new_password: formData.newPassword || null,
          iban: formData.iban,
          tax_id: formData.taxId,
          social_security_num: formData.socialSecurityNum
        })
      });
      const data = await response.json();
      if (response.ok) {
        setMessage({ type: 'success', text: 'Änderungen erfolgreich gespeichert!' });
        setFormData(prev => ({ ...prev, currentPassword: '', newPassword: '', confirmPassword: '' }));
        if (data.new_token) localStorage.setItem('tester_token', data.new_token);
      } else {
        setMessage({ type: 'error', text: data.detail || 'Fehler beim Speichern.' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Netzwerkfehler.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDownloadContract = async () => {
    try {
      const token = localStorage.getItem('tester_token');
      const response = await fetch('/api/employee/contract/download', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) return alert("Ihr Arbeitsvertrag steht noch nicht zur Verfügung. Bitte warten Sie, bis Ihr Account von einem Admin freigeschaltet wurde.");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Mein_ENVISU_Arbeitsvertrag.pdf');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Fehler beim Herunterladen des Vertrags.");
    }
  };

  if (isLoading) return <div className="flex justify-center items-center h-64"><Loader2 className="w-8 h-8 animate-spin text-accent" /></div>;

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 max-w-3xl">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-primary">Einstellungen</h1>
        <p className="text-muted-foreground">Verwalten Sie Ihre Profil- und Abrechnungsdaten für die Auszahlung.</p>
      </div>
      {message.text && (
        <div className={`p-4 rounded-md mb-6 border ${message.type === 'error' ? 'bg-red-50 text-red-600 border-red-200' : 'bg-green-50 text-green-700 border-green-200'}`}>
          {message.text}
        </div>
      )}
      
      <div className="bg-sky-50 border border-accent/30 rounded-xl p-6 mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="font-bold text-lg text-primary flex items-center gap-2">
            <FileText className="w-5 h-5 text-accent" /> Persönlicher Arbeitsvertrag
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Hier können Sie Ihren generierten Arbeitsvertrag mit allen festgelegten Konditionen herunterladen.</p>
        </div>
        <button
          type="button"
          onClick={handleDownloadContract}
          className="bg-accent text-white px-5 py-2.5 rounded-md font-medium hover:bg-accent/90 transition-colors flex items-center gap-2 shrink-0 shadow-sm"
        >
          <Download className="w-4 h-4" /> Vertrag Herunterladen
        </button>
      </div>

      <form onSubmit={handleSave} className="space-y-6">
        <div className="bg-white rounded-xl border border-border shadow-sm p-6">
          <h2 className="font-bold text-lg text-primary mb-4 flex items-center gap-2"><User className="w-5 h-5 text-accent" /> Zugangsdaten</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">E-Mail Adresse (Login)</label>
              <input type="email" required value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="w-full p-2.5 border border-border rounded focus:border-accent outline-none" />
            </div>
            <div className="pt-4 border-t border-slate-100">
              <h3 className="font-medium text-primary mb-3 flex items-center gap-2"><Lock className="w-4 h-4 text-slate-400" /> Passwort ändern (Optional)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Neues Passwort</label>
                  <input type="password" value={formData.newPassword} onChange={e => setFormData({...formData, newPassword: e.target.value})} className="w-full p-2.5 border border-border rounded focus:border-accent outline-none" placeholder="Min. 6 Zeichen" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Passwort wiederholen</label>
                  <input type="password" value={formData.confirmPassword} onChange={e => setFormData({...formData, confirmPassword: e.target.value})} className="w-full p-2.5 border border-border rounded focus:border-accent outline-none" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-border shadow-sm p-6 space-y-4">
          <h2 className="font-bold text-lg text-primary mb-2 flex items-center gap-2"><CreditCard className="w-5 h-5 text-accent" /> Auszahlungs- & Abrechnungsdaten</h2>
          <p className="text-xs text-muted-foreground mb-4">Diese Daten werden für die monatliche Minijob-Anmeldung und Gehaltsüberweisung benötigt.</p>
          <div>
            <label className="block text-sm font-medium mb-1">IBAN (Auszahlungskonto)</label>
            <input type="text" value={formData.iban} onChange={e => setFormData({...formData, iban: e.target.value})} className="w-full p-2.5 border border-border rounded focus:border-accent outline-none font-mono" placeholder="DE00 0000 0000 0000 0000 00" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Steuer-ID (11-stellig)</label>
              <input type="text" value={formData.taxId} onChange={e => setFormData({...formData, taxId: e.target.value})} className="w-full p-2.5 border border-border rounded focus:border-accent outline-none font-mono" placeholder="12 345 678 901" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sozialversicherungsnummer (SVNR)</label>
              <input type="text" value={formData.socialSecurityNum} onChange={e => setFormData({...formData, socialSecurityNum: e.target.value})} className="w-full p-2.5 border border-border rounded focus:border-accent outline-none font-mono" placeholder="65 120480 M 043" />
            </div>
          </div>
        </div>

        <div className="bg-slate-50 rounded-xl border border-border p-6 flex flex-col md:flex-row md:items-end justify-between gap-4">
          <div className="flex-1 max-w-sm">
            <label className="block text-sm font-bold text-primary mb-1">Aktuelles Passwort <span className="text-red-500">*</span></label>
            <input type="password" required value={formData.currentPassword} onChange={e => setFormData({...formData, currentPassword: e.target.value})} className="w-full p-2.5 border border-slate-300 rounded focus:border-accent outline-none" placeholder="Zur Bestätigung erforderlich" />
          </div>
          <button type="submit" disabled={isSaving} className="bg-primary text-white px-8 py-2.5 rounded-md font-medium hover:bg-secondary transition-colors flex items-center justify-center gap-2 disabled:opacity-70 h-[42px]">
            {isSaving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save className="w-5 h-5" />}
            Speichern
          </button>
        </div>
      </form>
    </div>
  );
}
